import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogCategory } from "@/app/types/BlogCategory";
import { BlogSlugs, PublicPaths } from "@/app/vars/Routes";

export const objectionHandlingArticle: BlogArticle = {
  key: BlogArticleKey.OBJECTION_HANDLING,
  slug: BlogSlugs.OBJECTION_HANDLING,
  categoryId: BlogCategory.CALL_ANALYSIS,
  date: "2026-09-29",
  readTime: 9,
  locales: {
    fr: {
      title: "Traiter les objections à partir de ce que disent vos clients",
      excerpt:
        "Les listes d'objections génériques sont écrites depuis l'imagination du vendeur. Voici comment bâtir la vôtre à partir des phrases réelles de vos clients, et garder les réponses qui marchent.",
      description:
        "Pourquoi les listes d'objections génériques échouent, et comment bâtir un playbook d'objections à partir de ce que vos clients disent dans vos appels.",
      ctaTitle: "Partez des objections de vos clients, pas d'un script",
      ctaText:
        "On relève les objections dans vos échanges, on repère celles qui ont été traitées et on compte celles qui reviennent, avec les passages exacts. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Presque toutes les équipes commerciales ont un document d'objections. Une vingtaine de lignes, « c'est trop cher », « on n'a pas le temps », « on a déjà un outil », chacune suivie d'une réponse bien tournée. Il a été écrit un après-midi, par la personne qui vendait le mieux à l'époque, et il n'a presque jamais été rouvert.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le problème n'est pas la qualité des réponses. C'est que les objections, elles, viennent de l'imagination du vendeur, pas de la bouche des clients. Et sur le terrain, un client ne dit presque jamais « c'est trop cher » de la façon dont le document l'avait prévu.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Pourquoi les listes d'objections génériques échouent",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une liste générique a trois défauts, et ils se cumulent.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Elle est écrite à la place du client.** Les formulations sont celles que le vendeur imagine, donc propres, directes, faciles à contrer. Les vraies objections sont plus floues : « il faut que je voie comment ça s'articule avec le reste », « on n'est pas sûrs que ce soit le bon moment pour l'équipe ».",
            "**Elle traite l'objection énoncée, pas l'objection réelle.** « C'est trop cher » peut vouloir dire que la valeur n'est pas démontrée, que le budget est ailleurs, ou que la personne n'a pas le pouvoir de signer. Trois situations, trois réponses différentes, une seule ligne dans le document.",
            "**Elle ne dit pas ce qui marche.** Une réponse écrite n'a jamais été testée. Personne ne sait si elle a fait avancer une seule conversation, ni sur quel type de client.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Une objection que vous n'avez jamais entendue telle quelle ne vous apprend rien. Celle que vos clients répètent avec leurs propres mots vous apprend presque tout.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La matière existe pourtant déjà : elle est dans vos appels. Chaque découverte, chaque démo, chaque négociation contient des objections formulées par de vrais acheteurs, et des réponses qui ont, ou n'ont pas, fait avancer l'échange.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Construire votre carte d'objections à partir des appels",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une carte d'objections n'est pas une liste de réponses. C'est un relevé de ce qui a été dit, classé, puis relié à ce qui s'est passé ensuite. Cinq étapes suffisent.",
        },
        {
          type: ArticleBlockType.H3,
          text: "1. Relever la formulation exacte",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Notez la phrase telle que le client l'a prononcée, pas votre résumé. « Trop cher » est un résumé. « Pour une équipe de six, ça fait beaucoup par rapport à ce qu'on paie aujourd'hui » est une objection : elle contient une taille d'équipe, une comparaison et un point de référence.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Gardez aussi le contexte minimal : quel client, quel type d'échange, à quel moment de l'appel. Sans lui, la phrase ne se relit plus six mois plus tard.",
        },
        {
          type: ArticleBlockType.H3,
          text: "2. Classer par famille",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Six familles couvrent l'essentiel des objections en vente B2B. Elles servent à regrouper, pas à enfermer : une même phrase peut relever de deux familles.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Prix** : le montant, le mode de facturation, la comparaison avec l'existant.",
            "**Timing** : pas maintenant, après le prochain trimestre, quand tel projet sera terminé.",
            "**Confiance** : la taille de votre entreprise, la sécurité des données, les références dans le secteur.",
            "**Statu quo** : « on s'en sort avec des tableurs », « ce n'est pas une priorité ».",
            "**Concurrent** : un outil déjà en place, une offre comparée, un fournisseur historique.",
            "**Décision interne** : le budget d'un autre service, un comité, une personne absente de l'appel.",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "3. Chercher l'objection derrière l'objection",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'objection énoncée est rarement la vraie. C'est souvent la plus facile à dire. Le tableau ci-dessous donne des lectures fréquentes et une question pour les vérifier, à titre d'illustration : la bonne lecture dépend toujours de l'appel.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que le client dit", "Ce que ça veut souvent dire, et quoi demander"],
          rows: [
            [
              "« C'est un peu cher pour nous. »",
              "La valeur n'est pas encore chiffrée. Demandez : « Par rapport à quoi le comparez-vous ? »",
            ],
            [
              "« Revenez vers nous au prochain trimestre. »",
              "Une autre priorité, ou pas de porteur interne. Demandez : « Qu'est-ce qui aura changé d'ici là ? »",
            ],
            [
              "« Il faut que j'en parle en interne. »",
              "La personne ne décide pas seule, ou n'est pas convaincue. Demandez : « Qui d'autre sera dans la décision, et qu'est-ce qui comptera pour eux ? »",
            ],
            [
              "« On a déjà un outil pour ça. »",
              "Statu quo ou concurrent installé. Demandez : « Qu'est-ce qui vous manque avec l'outil actuel ? »",
            ],
            [
              "« Vous êtes une petite structure. »",
              "Un doute sur la pérennité ou la sécurité. Demandez : « Qu'est-ce qui vous rassurerait concrètement ? »",
            ],
            [
              "« Envoyez-moi une documentation. »",
              "Souvent une sortie polie. Demandez : « Sur quel point précis la documentation doit-elle vous répondre ? »",
            ],
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "4. Noter ce qui a fait avancer la conversation",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "C'est l'étape que presque personne ne fait, et c'est la plus utile. Pour chaque objection relevée, regardez la suite de l'appel : le client a-t-il rebondi, posé une question concrète, accepté une prochaine étape ? Ou la conversation s'est-elle refroidie ?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Distinguez une objection **traitée**, où la réponse a débouché sur autre chose qu'un silence, d'une objection **seulement répondue**, où vous avez parlé et où le client est passé à autre chose sans être convaincu. Sur le moment, les deux se ressemblent. Relues, elles ne se ressemblent pas du tout.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Au bout de quelques dizaines d'appels, vous savez quelles réponses marchent vraiment. Elles sont souvent différentes de celles du document initial.",
        },
        {
          type: ArticleBlockType.H3,
          text: "5. Mesurer la fréquence par persona et par étape",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une objection n'a pas le même poids partout. Le prix peut dominer chez les petites équipes et presque disparaître chez les plus grandes, où la décision interne prend toute la place. La confiance peut peser en découverte et s'effacer après la démo.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Comptez donc les objections par groupe de clients et par étape du cycle. C'est ce découpage qui vous dit quoi préparer avant un appel donné, plutôt qu'une liste unique censée valoir pour tout le monde.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Une méthode pour répondre : accueillir, creuser, recadrer, confirmer",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une fois l'objection réelle identifiée, la réponse suit presque toujours le même mouvement en quatre temps.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Accueillir.** Reformulez sans contredire. Le client doit sentir que son objection est entendue, pas qu'elle déclenche une réplique préparée.",
            "**Creuser.** Posez une question avant de répondre. C'est elle qui fait apparaître l'objection réelle, et elle vous évite de répondre à la mauvaise.",
            "**Recadrer avec une preuve client.** Répondez avec ce qu'un client comparable a dit ou vécu, avec ses propres mots si possible. La phrase d'un pair pèse plus que votre argumentaire.",
            "**Confirmer.** Vérifiez que la réponse a porté : « Est-ce que cela répond à votre question, ou reste-t-il un point ? » Sans cette étape, vous ne savez pas si l'objection est traitée ou seulement mise de côté.",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Exemple illustratif : une objection de prix",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*Le dialogue suivant est une illustration, pas la transcription d'un vrai appel.*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Client** : « Pour une équipe de six, ça fait beaucoup par rapport à ce qu'on paie aujourd'hui. »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Commercial** : « Je comprends, vous comparez avec votre outil actuel. Que payez-vous aujourd'hui, et qu'est-ce que cela couvre ? »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Client** : « Pas grand-chose, en réalité. Les comptes rendus, on les refait à la main après chaque appel. »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Commercial** : « Un client de taille comparable nous disait exactement cela : son équipe passait la fin de journée à rédiger des comptes rendus que personne ne relisait. Combien de temps cela vous prend-il ? Et est-ce ce point qui compte le plus dans votre comparaison ? »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'objection énoncée portait sur le prix. L'objection réelle portait sur une valeur que personne n'avait chiffrée. La question l'a fait apparaître, la preuve client l'a recadrée, la dernière question vérifie qu'elle a porté.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Exemple illustratif : une objection de timing",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Client** : « C'est intéressant, mais revenez vers nous en janvier. »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Commercial** : « Entendu. Qu'est-ce qui sera différent en janvier ? »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Client** : « Notre nouveau directeur commercial arrive, c'est lui qui décidera. »",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Ici, le timing masquait une décision interne. La bonne suite n'est pas une relance dans trois mois : c'est de proposer un échange avec la personne qui arrive, ou de préparer ce qu'elle voudra voir.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Du script figé à la carte vivante",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Le document d'objections habituel",
            items: [
              "Écrit une fois, depuis l'imagination du vendeur",
              "Des formulations propres, faciles à contrer",
              "Une réponse par objection, pour tous les clients",
              "Aucune trace de ce qui a marché",
            ],
          },
          good: {
            title: "Une carte construite à partir des appels",
            items: [
              "Alimentée en continu par les phrases réelles des clients",
              "Chaque objection reliée à l'objection réelle qu'elle cache",
              "Des réponses différentes selon le persona et l'étape",
              "Seules restent les réponses qui ont fait avancer l'échange",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "Faire vivre le playbook : une revue par mois",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Une carte d'objections se périme. Un nouveau concurrent apparaît, un changement de prix déplace les objections, un nouveau segment apporte les siennes. Une revue mensuelle d'une heure suffit à la garder juste, autour de cinq questions.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Quelles objections sont apparues ce mois-ci, et lesquelles ont disparu ?",
            "Lesquelles reviennent le plus, et chez quels clients ?",
            "Quelles réponses ont fait avancer l'échange, avec la phrase exacte du client ?",
            "Quels concurrents sont cités, et dans quels termes ?",
            "Que retire-t-on du playbook parce que plus personne ne le dit ?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Faire ce travail à la main demande de réécouter des heures d'appels. C'est pour cela qu'on le fait pour vous : chaque échange est transcrit puis analysé, et les passages où le client objecte, ceux où l'objection est traitée et ceux où un concurrent est mentionné sont tagués. L'assistant peut alors compter les objections qui reviennent le plus, en citant pour chacune les passages exacts dont il tire le compte. Et les personas montrent les thèmes récurrents de chaque groupe de clients, avec leurs propres mots.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Demandez à Meidly quelles objections reviennent le plus",
          text: "L'assistant compte les objections à partir des passages tagués de vos échanges, par persona, par étape ou par période, et cite chaque passage sur lequel il s'appuie.",
          linkLabel: "Ouvrir l'assistant",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La décision reste la vôtre : un tag est une proposition, et c'est l'équipe qui choisit quelle réponse entre dans le playbook.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Checklist",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Relevez la phrase exacte du client, pas votre résumé.",
            "Classez-la dans une famille : prix, timing, confiance, statu quo, concurrent, décision interne.",
            "Écrivez l'objection réelle que vous soupçonnez derrière, et la question qui la vérifie.",
            "Notez si la réponse a fait avancer l'échange ou non.",
            "Comptez les objections par persona et par étape du cycle.",
            "Gardez dans le playbook les réponses qui ont marché, avec la phrase client qui le prouve.",
            "Revoyez la carte une fois par mois, et retirez ce que plus personne ne dit.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le meilleur document d'objections n'est pas le mieux écrit. C'est celui dont chaque ligne vient d'un vrai appel, et dont chaque réponse a déjà fait ses preuves.",
        },
      ],
      faq: [
        {
          question: "Qu'est-ce qu'une carte d'objections ?",
          answer:
            "C'est un relevé des objections réellement formulées par vos clients, avec leur phrase exacte, leur famille (prix, timing, confiance, statu quo, concurrent, décision interne), l'objection réelle qu'elles cachent et les réponses qui ont fait avancer l'échange. Contrairement à un script, elle part de ce qui a été dit, pas de ce que le vendeur imagine.",
        },
        {
          question: "Combien d'appels faut-il pour construire une carte fiable ?",
          answer:
            "Quelques dizaines d'appels suffisent à faire apparaître les objections qui reviennent. En dessous, vous observez surtout des anecdotes : une objection entendue une seule fois ne justifie pas de modifier votre playbook.",
        },
        {
          question: "Faut-il abandonner les scripts de réponse ?",
          answer:
            "Non, mais il faut les construire autrement. Une réponse mérite sa place quand elle a fait avancer de vrais échanges, et elle gagne à s'appuyer sur la phrase d'un client comparable plutôt que sur un argumentaire.",
        },
        {
          question: "Comment savoir si une objection a vraiment été traitée ?",
          answer:
            "Regardez la suite de l'appel. Une objection traitée débouche sur une question concrète, une précision ou une prochaine étape acceptée. Une objection seulement répondue laisse place à un changement de sujet ou à un silence. Terminer par une question de confirmation vous aide à trancher sur le moment.",
        },
        {
          question: "À quelle fréquence mettre à jour le playbook d'objections ?",
          answer:
            "Une revue mensuelle suffit dans la plupart des équipes. Elle sert à ajouter les objections apparues, à retirer celles que plus personne ne formule et à remplacer les réponses qui ne font plus avancer les échanges.",
        },
      ],
    },
    en: {
      title: "Handling objections from what your customers actually say",
      excerpt:
        "Generic objection lists are written from the seller's imagination. Here is how to build your own from your customers' real words, and keep the answers that work.",
      description:
        "Why generic objection lists fail, and how to build an objection playbook from what your customers actually say on your sales calls.",
      ctaTitle: "Start from your customers' objections, not a script",
      ctaText:
        "We surface the objections in your exchanges, spot the ones that were handled and count the ones that keep coming back, with the exact passages. No commitment.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Almost every sales team has an objection document. Twenty or so lines, “it's too expensive”, “we don't have time”, “we already have a tool”, each followed by a well turned answer. It was written one afternoon by whoever sold best at the time, and it has hardly been opened since.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The problem is not the quality of the answers. It is that the objections come from the seller's imagination, not from customers' mouths. And in the field, a customer almost never says “it's too expensive” the way the document expected.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Why generic objection lists fail",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "A generic list has three flaws, and they add up.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**It is written on the customer's behalf.** The wordings are the ones the seller imagines, so they are clean, direct and easy to counter. Real objections are fuzzier: “I need to see how it fits with everything else”, “we are not sure it is the right time for the team”.",
            "**It handles the stated objection, not the real one.** “It's too expensive” can mean the value has not been shown, the budget sits elsewhere, or the person cannot sign. Three situations, three different answers, one line in the document.",
            "**It does not say what works.** A written answer has never been tested. Nobody knows whether it ever moved a single conversation forward, or with which kind of customer.",
          ],
        },
        {
          type: ArticleBlockType.LEAD,
          text: "An objection you have never heard in those words teaches you nothing. The one your customers repeat in their own words teaches you almost everything.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The material already exists: it is in your calls. Every discovery, every demo, every negotiation holds objections voiced by real buyers, and answers that did, or did not, move the exchange forward.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Building your objection map from your calls",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection map is not a list of answers. It is a record of what was said, classified, then linked to what happened next. Five steps are enough.",
        },
        {
          type: ArticleBlockType.H3,
          text: "1. Capture the exact wording",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Write down the sentence the way the customer said it, not your summary. “Too expensive” is a summary. “For a team of six, that is a lot compared to what we pay today” is an objection: it holds a team size, a comparison and a reference point.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Keep the minimum context too: which customer, which type of exchange, at what point in the call. Without it, the sentence no longer reads six months later.",
        },
        {
          type: ArticleBlockType.H3,
          text: "2. Classify by family",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Six families cover most objections in B2B sales. They are there to group, not to box in: one sentence can belong to two families.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Price**: the amount, the billing model, the comparison with what is in place.",
            "**Timing**: not now, after next quarter, once some project is done.",
            "**Trust**: the size of your company, data security, references in the sector.",
            "**Status quo**: “we get by with spreadsheets”, “it's not a priority”.",
            "**Competitor**: a tool already in place, a competing offer, a long standing supplier.",
            "**Internal decision**: another department's budget, a committee, someone who was not on the call.",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "3. Look for the objection behind the objection",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The stated objection is rarely the real one. It is often the easiest one to say. The table below gives common readings and a question to check them, as an illustration: the right reading always depends on the call.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What the customer says", "What it often means, and what to ask"],
          rows: [
            [
              "“It's a bit expensive for us.”",
              "The value has not been quantified yet. Ask: “What are you comparing it with?”",
            ],
            [
              "“Get back to us next quarter.”",
              "Another priority, or no internal sponsor. Ask: “What will have changed by then?”",
            ],
            [
              "“I need to discuss it internally.”",
              "The person does not decide alone, or is not convinced. Ask: “Who else will be in the decision, and what will matter to them?”",
            ],
            [
              "“We already have a tool for that.”",
              "Status quo or an installed competitor. Ask: “What is missing with your current tool?”",
            ],
            [
              "“You are a small company.”",
              "A doubt about continuity or security. Ask: “What would reassure you, concretely?”",
            ],
            [
              "“Send me some documentation.”",
              "Often a polite exit. Ask: “Which specific point should the documentation answer for you?”",
            ],
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "4. Note what moved the conversation forward",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "This is the step almost nobody takes, and it is the most useful one. For each objection you capture, look at what followed in the call: did the customer pick it up, ask a concrete question, agree to a next step? Or did the conversation cool down?",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tell apart an objection that was **handled**, where the answer led to something other than silence, from one that was **merely answered**, where you spoke and the customer moved on without being convinced. In the moment, the two look alike. On review, they look nothing alike.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "After a few dozen calls, you know which answers really work. They are often different from the ones in the original document.",
        },
        {
          type: ArticleBlockType.H3,
          text: "5. Measure frequency by persona and by stage",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection does not weigh the same everywhere. Price can dominate with small teams and nearly vanish with larger ones, where the internal decision takes all the room. Trust can weigh during discovery and fade after the demo.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "So count objections by group of customers and by stage of the cycle. That split is what tells you what to prepare before a given call, rather than one list meant to fit everyone.",
        },
        {
          type: ArticleBlockType.H2,
          text: "A method to answer: acknowledge, dig, reframe, confirm",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Once the real objection is identified, the answer almost always follows the same four beat movement.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Acknowledge.** Rephrase without contradicting. The customer should feel their objection was heard, not that it triggered a prepared comeback.",
            "**Dig.** Ask a question before answering. That question is what brings out the real objection, and it saves you from answering the wrong one.",
            "**Reframe with customer proof.** Answer with what a comparable customer said or went through, in their own words if possible. A peer's sentence weighs more than your pitch.",
            "**Confirm.** Check that the answer landed: “Does that answer your question, or is there something left?” Without this step, you do not know whether the objection was handled or just set aside.",
          ],
        },
        {
          type: ArticleBlockType.H3,
          text: "Illustrative example: a price objection",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "*The following dialogue is an illustration, not the transcript of a real call.*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Customer**: “For a team of six, that is a lot compared to what we pay today.”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Seller**: “I understand, you are comparing it with your current tool. What do you pay today, and what does it cover?”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Customer**: “Not much, to be honest. We rewrite the call notes by hand after every call.”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Seller**: “A customer of a similar size told us exactly that: their team spent the end of each day writing notes nobody read. How much time does it take you? And is that the point that matters most in your comparison?”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The stated objection was about price. The real one was about a value nobody had quantified. The question brought it out, the customer proof reframed it, and the last question checks that it landed.",
        },
        {
          type: ArticleBlockType.H3,
          text: "Illustrative example: a timing objection",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Customer**: “It's interesting, but get back to us in January.”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Seller**: “Understood. What will be different in January?”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "**Customer**: “Our new head of sales starts then, and they will make the call.”",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Here, timing was hiding an internal decision. The right next step is not a follow-up in three months: it is to offer a conversation with the incoming person, or to prepare what they will want to see.",
        },
        {
          type: ArticleBlockType.H2,
          text: "From a frozen script to a living map",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "The usual objection document",
            items: [
              "Written once, from the seller's imagination",
              "Clean wordings, easy to counter",
              "One answer per objection, for every customer",
              "No trace of what worked",
            ],
          },
          good: {
            title: "A map built from your calls",
            items: [
              "Fed continuously by customers' real sentences",
              "Each objection linked to the real one it hides",
              "Different answers by persona and by stage",
              "Only the answers that moved the exchange forward stay",
            ],
          },
        },
        {
          type: ArticleBlockType.H2,
          text: "Keeping the playbook alive: one review a month",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "An objection map goes stale. A new competitor appears, a price change shifts the objections, a new segment brings its own. A one hour monthly review is enough to keep it accurate, around five questions.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Which objections appeared this month, and which disappeared?",
            "Which come up most, and with which customers?",
            "Which answers moved the exchange forward, with the customer's exact sentence?",
            "Which competitors are mentioned, and in what terms?",
            "What do we remove from the playbook because nobody says it anymore?",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Doing this by hand means listening back to hours of calls. That is why we do it for you: every exchange is transcribed then analysed, and the passages where the customer objects, where the objection is handled and where a competitor is mentioned are tagged. The assistant can then count which objections come up most, citing for each one the exact passages the count comes from. And personas show the recurring themes of each group of customers, in their own words.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Ask Meidly which objections come up most",
          text: "The assistant counts objections from the tagged passages of your exchanges, by persona, by stage or by period, and cites every passage it relies on.",
          linkLabel: "Open the assistant",
          linkPath: PublicPaths.HOME,
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The decision stays yours: a tag is a suggestion, and the team chooses which answer goes into the playbook.",
        },
        {
          type: ArticleBlockType.H2,
          text: "Checklist",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "Capture the customer's exact sentence, not your summary.",
            "Put it in a family: price, timing, trust, status quo, competitor, internal decision.",
            "Write down the real objection you suspect behind it, and the question that checks it.",
            "Note whether the answer moved the exchange forward or not.",
            "Count objections by persona and by stage of the cycle.",
            "Keep in the playbook the answers that worked, with the customer sentence that proves it.",
            "Review the map once a month, and remove what nobody says anymore.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The best objection document is not the best written one. It is the one where every line comes from a real call, and every answer has already proved itself.",
        },
      ],
      faq: [
        {
          question: "What is an objection map?",
          answer:
            "It is a record of the objections your customers actually voiced, with their exact sentence, their family (price, timing, trust, status quo, competitor, internal decision), the real objection they hide and the answers that moved the exchange forward. Unlike a script, it starts from what was said, not from what the seller imagines.",
        },
        {
          question: "How many calls does a reliable map take?",
          answer:
            "A few dozen calls are enough for recurring objections to appear. Below that you mostly see anecdotes: an objection heard once does not justify changing your playbook.",
        },
        {
          question: "Should you drop answer scripts altogether?",
          answer:
            "No, but they should be built differently. An answer earns its place when it has moved real exchanges forward, and it is stronger when it leans on a comparable customer's words rather than on a pitch.",
        },
        {
          question: "How do you know an objection was really handled?",
          answer:
            "Look at what followed in the call. A handled objection leads to a concrete question, a clarification or an agreed next step. An objection that was merely answered is followed by a change of subject or a silence. Ending with a confirmation question helps you tell them apart in the moment.",
        },
        {
          question: "How often should the objection playbook be updated?",
          answer:
            "A monthly review is enough for most teams. It is where you add the objections that appeared, remove the ones nobody voices anymore and replace the answers that no longer move exchanges forward.",
        },
      ],
    },
  },
};
